import {
  ApartmentOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuOutlined,
  PlusOutlined,
  SettingOutlined,
  ShopOutlined,
  SwapOutlined,
  TableOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Badge, Button, Grid, Layout, Menu, theme, Typography } from 'antd';
import { useEffect, useMemo, useState, type CSSProperties, type JSX, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { StickyTableProvider, type TableSticky } from '../context/StickyTableContext';
import { useStoreContext } from '../context/StoreContext';
import { BusinessSelector } from './BusinessSelector';
import { ModalSelect } from './ModalSelect';
import { usePendingApprovals } from '../hooks/usePendingApprovalsCount';
import { usePendingShipmentsCount } from '../hooks/usePendingShipmentsCount';
import { QuickEntryDrawer } from './QuickEntryDrawer';
import { markJustSubmitted } from '../lib/justSubmitted';
import { SellerEntryDrawer } from './SellerEntryDrawer';

const HEADER_HEIGHT = 56;
const TABBAR_HEIGHT = 60;

/** Global store-context selector (SPEC §5): «Администратор» or one store. */
function StoreSelector({ style }: { style?: CSSProperties }): JSX.Element {
  const { t } = useTranslation();
  const { scope, setScope, stores, storesLoading, scopeUsers } = useStoreContext();
  const storeName = (id: string): string => stores.find((s) => s.id === id)?.name ?? '…';
  const value =
    scope.kind === 'store'
      ? scope.storeId
      : scope.kind === 'user'
        ? `user:${scope.userId}`
        : '__admin__';
  return (
    <ModalSelect
      value={value}
      onChange={(v) =>
        setScope(
          !v || v === '__admin__'
            ? { kind: 'all' }
            : v.startsWith('user:')
              ? { kind: 'user', userId: v.slice('user:'.length) }
              : { kind: 'store', storeId: v },
        )
      }
      loading={storesLoading}
      size="middle"
      variant="filled"
      title={t('common.store')}
      style={{ minWidth: 160, maxWidth: 220, ...style }}
      options={[
        { value: '__admin__', label: t('common.adminContext') },
        // Owner-only: «как видит пользователь X» — the person, their stores beneath.
        ...scopeUsers.map((u) => ({
          value: `user:${u.id}`,
          label: u.name,
          description: u.storeIds.map(storeName).join(', '),
        })),
        ...stores.map((s) => ({ value: s.id, label: s.name })),
      ]}
    />
  );
}

interface NavItem {
  key: string;
  labelKey: string;
  icon: ReactNode;
}

const TABS: NavItem[] = [
  { key: '/', labelKey: 'nav.home', icon: <HomeOutlined /> },
  { key: '/stores', labelKey: 'nav.stores', icon: <ShopOutlined /> },
  { key: '/sklads', labelKey: 'nav.sklads', icon: <DatabaseOutlined /> },
  { key: '/operations', labelKey: 'nav.journal', icon: <UnorderedListOutlined /> },
  { key: '/more', labelKey: 'nav.more', icon: <MenuOutlined /> },
];

const SIDER_ITEMS: NavItem[] = [
  ...TABS.slice(0, 4),
  { key: '/shipments', labelKey: 'nav.shipments', icon: <FileDoneOutlined /> },
  { key: '/movements', labelKey: 'nav.movements', icon: <SwapOutlined /> },
  { key: '/approvals', labelKey: 'nav.approvals', icon: <CheckCircleOutlined /> },
  { key: '/reports', labelKey: 'nav.reports', icon: <FileTextOutlined /> },
  { key: '/products', labelKey: 'nav.products', icon: <BarChartOutlined /> },
  { key: '/summary', labelKey: 'nav.summary', icon: <TableOutlined /> },
  { key: '/settings', labelKey: 'nav.settings', icon: <SettingOutlined /> },
];

// A SELLER sees only their store and the накладные they price.
const SELLER_NAV: NavItem[] = [
  { key: '/', labelKey: 'nav.myStore', icon: <ShopOutlined /> },
  { key: '/shipments', labelKey: 'nav.shipments', icon: <FileDoneOutlined /> },
];

// The PLATFORM_ADMIN's cross-tenant item (desktop sider; /more on mobile).
const BUSINESSES_ITEM: NavItem = {
  key: '/businesses',
  labelKey: 'nav.businesses',
  icon: <ApartmentOutlined />,
};

// Before the platform admin picks a business there is exactly one page.
const PLATFORM_NAV: NavItem[] = [BUSINESSES_ITEM];

function makeActiveKey(items: NavItem[]) {
  return (pathname: string): string => {
    if (pathname === '/') return '/';
    const match = items
      .map((i) => i.key)
      .filter((k) => k !== '/' && pathname.startsWith(k))
      .sort((a, b) => b.length - a.length)[0];
    return (
      match ??
      (pathname.startsWith('/settings') ||
      pathname.startsWith('/shipments') ||
      pathname.startsWith('/movements') ||
      pathname.startsWith('/businesses')
        ? '/more'
        : '/')
    );
  };
}

/**
 * Responsive shell (SPEC §5): fixed top bar with the store-context selector,
 * bottom tab bar below `lg`, Layout.Sider from `lg` up, central "+" FAB
 * everywhere opening the quick-entry drawer. Role-aware: a SELLER gets a
 * two-item nav (their store + накладные), no store selector, and the seller
 * entry drawer.
 */
export function AppShell(): JSX.Element {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const desktop = screens.lg ?? false;
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isStoreRestricted, isPlatformAdmin, shellMode: mode } = useAuth();
  const [quickOpen, setQuickOpen] = useState(false);

  const isSeller = mode === 'seller';
  // No business selected (platform admin): no store scope, no money queries,
  // no quick entry — just the businesses page and the picker.
  const noBusiness = mode === 'platform-no-business';
  const isAdmin = !isSeller && !noBusiness;
  const pendingShipments = usePendingShipmentsCount(!noBusiness);

  // Callback ref (state) rather than useRef so the sticky-table context below
  // re-renders once the Content element mounts and can hand tables a real
  // `getContainer`.
  const [contentEl, setContentEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    contentEl?.scrollTo(0, 0);
    window.scrollTo(0, 0);
  }, [location.pathname, contentEl]);

  // Sticky column headers for page-level tables. Desktop scrolls inside the
  // Content box; mobile scrolls the window beneath the fixed app header.
  const tableSticky = useMemo<TableSticky>(
    () =>
      desktop
        ? { offsetHeader: 0, getContainer: () => contentEl ?? window }
        : { offsetHeader: HEADER_HEIGHT },
    [desktop, contentEl],
  );

  // Sklad pages are whole-business (supplier debt) — hidden from a
  // store-restricted USER (the backend refuses them too).
  const forAdmin = (items: NavItem[]): NavItem[] =>
    isStoreRestricted ? items.filter((i) => i.key !== '/sklads') : items;
  const bottomNav = noBusiness ? PLATFORM_NAV : isSeller ? SELLER_NAV : forAdmin(TABS);
  const siderItems = noBusiness
    ? PLATFORM_NAV
    : isSeller
      ? SELLER_NAV
      : [...forAdmin(SIDER_ITEMS), ...(isPlatformAdmin ? [BUSINESSES_ITEM] : [])];
  const activeKey = makeActiveKey(siderItems)(location.pathname);

  const pageTitleKey = ((): string => {
    if (location.pathname.startsWith('/businesses')) return 'nav.businesses';
    if (location.pathname.startsWith('/reports')) return 'nav.reports';
    if (location.pathname.startsWith('/settings')) return 'nav.settings';
    if (location.pathname.startsWith('/approvals')) return 'nav.approvals';
    if (location.pathname.startsWith('/shipments')) return 'nav.shipments';
    if (location.pathname.startsWith('/movements')) return 'nav.movements';
    return siderItems.find((i) => i.key === activeKey)?.labelKey ?? 'app.title';
  })();

  // Admin approval backlog — badge on the «Тасдиклар» item (and the mobile More
  // tab, alongside pending shipments).
  const { total: pendingApprovals } = usePendingApprovals(isAdmin);
  const badgeFor = (key: string): number => {
    if (isSeller) return 0;
    if (key === '/shipments') return pendingShipments;
    if (key === '/approvals') return pendingApprovals;
    if (key === '/more') return pendingShipments + pendingApprovals;
    return 0;
  };

  const fab = (
    <Button
      type="primary"
      shape="circle"
      aria-label={t('quick.title')}
      icon={<PlusOutlined style={{ fontSize: 22 }} />}
      onClick={() => setQuickOpen(true)}
      style={{
        position: 'fixed',
        width: 56,
        height: 56,
        boxShadow: '0 4px 14px rgba(0,0,0,0.28)',
        zIndex: 60,
        ...(desktop
          ? { right: 32, bottom: 32 }
          : {
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: `calc(${TABBAR_HEIGHT + 14}px + env(safe-area-inset-bottom))`,
            }),
      }}
    />
  );

  const drawer = noBusiness ? null : isSeller ? (
    <SellerEntryDrawer
      open={quickOpen}
      onClose={() => setQuickOpen(false)}
      onSubmitted={(req) => markJustSubmitted(req.id)}
    />
  ) : (
    <QuickEntryDrawer open={quickOpen} onClose={() => setQuickOpen(false)} />
  );
  const showFab = !noBusiness;

  if (desktop) {
    return (
      <Layout style={{ height: '100vh', overflow: 'hidden' }}>
        <Layout.Sider
          theme="light"
          width={248}
          style={{
            height: '100vh',
            overflowY: 'auto',
            borderRight: `1px solid ${token.colorSplit}`,
          }}
        >
          {/* Full-height flex column so the logout block (marginTop:auto) is
              pinned to the very bottom of the sidebar. */}
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ padding: '16px 16px 8px' }}>
              <Typography.Title level={4} style={{ margin: '0 0 12px' }}>
                {t('app.title')}
              </Typography.Title>
              {isPlatformAdmin ? (
                <BusinessSelector style={{ width: '100%', maxWidth: '100%', marginBottom: 8 }} />
              ) : null}
              {isAdmin ? <StoreSelector style={{ width: '100%', maxWidth: '100%' }} /> : null}
            </div>
            <Menu
              mode="inline"
              selectedKeys={[activeKey]}
              onClick={(e) => navigate(e.key)}
              style={{ borderInlineEnd: 'none' }}
              items={siderItems.map((i) => {
                const badge = badgeFor(i.key);
                return {
                  key: i.key,
                  icon: i.icon,
                  label:
                    badge > 0 ? (
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        {t(i.labelKey)}
                        <Badge count={badge} size="small" />
                      </span>
                    ) : (
                      t(i.labelKey)
                    ),
                };
              })}
            />
            <div style={{ padding: 16, marginTop: 'auto' }}>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                {user?.name}
              </Typography.Text>
              <Button icon={<LogoutOutlined />} onClick={() => void logout()} block>
                {t('common.logout')}
              </Button>
            </div>
          </div>
        </Layout.Sider>
        <Layout.Content
          ref={setContentEl}
          style={{ height: '100vh', overflowY: 'auto', padding: 24 }}
        >
          <div style={{ maxWidth: 960, margin: '0 auto' }}>
            <StickyTableProvider value={tableSticky}>
              <Outlet />
            </StickyTableProvider>
          </div>
          {showFab ? fab : null}
          {drawer}
        </Layout.Content>
      </Layout>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: `calc(${HEADER_HEIGHT}px + env(safe-area-inset-top))`,
          paddingTop: 'env(safe-area-inset-top)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '0 12px',
          background: token.colorBgContainer,
          borderBottom: `1px solid ${token.colorSplit}`,
          zIndex: 50,
        }}
      >
        <Typography.Text strong style={{ fontSize: 17, whiteSpace: 'nowrap' }}>
          {t(pageTitleKey)}
        </Typography.Text>
        {noBusiness ? (
          <BusinessSelector style={{ flex: '0 1 200px' }} />
        ) : isAdmin ? (
          <StoreSelector style={{ flex: '0 1 200px' }} />
        ) : null}
      </header>

      <main
        style={{
          paddingTop: `calc(${HEADER_HEIGHT + 12}px + env(safe-area-inset-top))`,
          paddingInline: 12,
          paddingBottom: `calc(${TABBAR_HEIGHT + 88}px + env(safe-area-inset-bottom))`,
        }}
      >
        <StickyTableProvider value={tableSticky}>
          <Outlet />
        </StickyTableProvider>
      </main>

      <nav
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: `calc(${TABBAR_HEIGHT}px + env(safe-area-inset-bottom))`,
          paddingBottom: 'env(safe-area-inset-bottom)',
          display: 'grid',
          gridTemplateColumns: `repeat(${bottomNav.length}, 1fr)`,
          background: token.colorBgContainer,
          borderTop: `1px solid ${token.colorSplit}`,
          zIndex: 50,
        }}
      >
        {bottomNav.map((tab) => {
          const active = activeKey === tab.key;
          const badge = badgeFor(tab.key);
          return (
            <NavLink
              key={tab.key}
              to={tab.key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                fontSize: 11,
                color: active ? token.colorPrimary : token.colorTextSecondary,
                textDecoration: 'none',
              }}
            >
              <span style={{ fontSize: 21 }}>
                {badge > 0 ? (
                  <Badge count={badge} size="small" offset={[2, 0]}>
                    <span style={{ fontSize: 21, color: 'inherit' }}>{tab.icon}</span>
                  </Badge>
                ) : (
                  tab.icon
                )}
              </span>
              {t(tab.labelKey)}
            </NavLink>
          );
        })}
      </nav>

      {showFab ? fab : null}
      {drawer}
    </div>
  );
}
